import React from 'react';
import { Link, Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import { Button, Dimmer, Dropdown, Form, Icon, Loader, Pagination, Table } from 'semantic-ui-react';
import MyList, { ADMIN_LIST_PAGINATION_ROW_CLASS } from './list';
import './product.list.scss';
import ProductListColumnsModal from './products-filter/product.list.columns-modal';
import ProductListFilterModal from './products-filter/product.list.filter-modal';
import {
    activeFilterCount,
    cloneFilterRows,
    filterFieldsToRows,
    parseProductFilterParams,
    ProductFilterRow,
    productListRequestBody,
    rowsToFilterFields,
    sanitizeFilterRows,
    syncFilterQueryParams,
} from './products-filter/product.list.filters';
import {
    loadProductListColumnVisibility,
    ProductListColumnId,
    saveProductListColumnVisibility,
} from './products-filter/product.list.table-columns';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/** Display API `saleUntil` (ISO / date string) in Mongolian locale. */
function formatProductSaleUntil(raw: string | null | undefined): string {
    if (raw == null || String(raw).trim() === '') return '—';
    const s = String(raw).trim();
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const d = ymd
        ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
        : new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('mn-MN', { year: 'numeric', month: 'long', day: 'numeric' });
}

function parseProductListSearch(search: string) {
    const params = new URLSearchParams(search);
    const sizeRaw = parseInt(params.get('size') || '25', 10);
    const size = PAGE_SIZE_OPTIONS.includes(sizeRaw) ? sizeRaw : 25;
    const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
    const providerRaw = params.get('providerId');
    let value1: number | null = null;
    if (providerRaw != null && providerRaw !== '') {
        const n = Number(providerRaw);
        if (!isNaN(n)) {
            value1 = n;
        }
    }
    return {
        value1,
        value2: params.get('name') ?? '',
        value3: params.get('barCode') ?? '',
        productFilterRows: filterFieldsToRows(parseProductFilterParams(search)),
        productFilterModalOpen: false,
        productFilterDraftRows: [] as ProductFilterRow[],
        page: { page, size, totalPages: 1, totalRows: 0 },
    };
}

class AdminProductList extends MyList {
    entity = "product";

    public previousNameValue = "";

    constructor(props: RouteComponentProps) {
        super(props);
        const productTableColumnVisibility = loadProductListColumnVisibility();
        this.state = {
            loading: true,
            data: [],
            errorMsg: '',
            redirect: '',
            list1: [],
            ...parseProductListSearch(props.location.search),
            productTableColumnVisibility,
            productColumnsModalOpen: false,
            productColumnsModalDraft: productTableColumnVisibility,
        };

        this.onPageChange = this.onPageChange.bind(this);
        this.onPageSizeChange = this.onPageSizeChange.bind(this);
        this.openFilterModal = this.openFilterModal.bind(this);
        this.closeFilterModal = this.closeFilterModal.bind(this);
        this.applyFilterModal = this.applyFilterModal.bind(this);
        this.openProductColumnsModal = this.openProductColumnsModal.bind(this);
        this.closeProductColumnsModal = this.closeProductColumnsModal.bind(this);
        this.applyProductColumnsModal = this.applyProductColumnsModal.bind(this);
        this.downloadCsv = this.downloadCsv.bind(this);
    }

    private toCsvCell(value: unknown): string {
        if (value == null) return '';
        const raw =
            typeof value === 'object'
                ? JSON.stringify(value)
                : String(value);
        return `"${raw.replace(/"/g, '""')}"`;
    }

    private downloadBlob(content: string, filename: string, mimeType: string) {
        const blob = new Blob([content], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    private async fetchProductPage(token: string, page: number, size: number) {
        const response = await fetch(process.env.REACT_APP_API_URL + "mongol/products", {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(productListRequestBody(this.state, { page, size })),
        });

        if (response.status === 401) {
            this.setState({ redirect: this.loginUrl });
            return null;
        }

        if (!response.ok) {
            throw new Error(`Failed to fetch page ${page}`);
        }

        return response.json();
    }

    private csvColumns() {
        return [
            { id: 'index' as ProductListColumnId, header: '№', value: (_row: any, index: number) => String(index + 1) },
            { id: 'name' as ProductListColumnId, header: 'Нэр', value: (row: any) => row?.name ?? '' },
            { id: 'provider' as ProductListColumnId, header: 'Нийлүүлэгч', value: (row: any) => row?.providerName ?? '' },
            {
                id: 'price' as ProductListColumnId,
                header: 'Үнэ',
                value: (row: any) => `${row?.saleActive ? row?.salePrice : row?.price ?? ''} ₮`,
            },
            { id: 'clubPrice' as ProductListColumnId, header: 'Клубын үнэ', value: (row: any) => `${row?.clubPrice ?? ''} ₮` },
            { id: 'salePrice' as ProductListColumnId, header: 'Хямдралтай үнэ', value: (row: any) => `${row?.salePrice ?? ''} ₮` },
            { id: 'saleUntil' as ProductListColumnId, header: 'Хямдралын сүүлийн өдөр', value: (row: any) => formatProductSaleUntil(row?.saleUntil) },
            {
                id: 'deliveryCost' as ProductListColumnId,
                header: 'Хүргэлтийн зардал',
                value: (row: any) => (row?.deliveryCost > 0 ? `${row.deliveryCost} ₮` : '-'),
            },
            { id: 'size' as ProductListColumnId, header: 'Хэмжээ', value: (row: any) => `${row?.size ?? ''} ${row?.unit ?? ''}`.trim() },
            { id: 'count' as ProductListColumnId, header: 'Үлдэгдэл', value: (row: any) => row?.count ?? '' },
            {
                id: 'stockLink' as ProductListColumnId,
                header: '',
                value: (row: any) =>
                    row?.warehouseId
                        ? (row?.import
                            ? `/mongol/warehouse/${row.warehouseId}?from=products`
                            : `/mongol/warehouse/${row.warehouseId}/count?from=products`)
                        : '',
            },
            { id: 'importCost' as ProductListColumnId, header: 'Өртөг тооцдог', value: (row: any) => (row?.import ? 'Тийм' : 'Үгүй') },
            { id: 'deliveryDayMargin' as ProductListColumnId, header: 'Хүргэх өдөр', value: (row: any) => row?.deliveryDayMargin ?? '' },
            { id: 'barCode' as ProductListColumnId, header: 'Бар код', value: (row: any) => row?.barCode ?? '' },
            {
                id: 'slugLink' as ProductListColumnId,
                header: 'Линк',
                value: (row: any) => (row?.slug ? `${window.location.origin}/product/${row.slug}` : ''),
            },
            { id: 'active' as ProductListColumnId, header: 'Идэвхитэй', value: (row: any) => (row?.active ? 'Тийм' : 'Үгүй') },
            { id: 'actions' as ProductListColumnId, header: '', value: () => '' },
        ];
    }

    async downloadCsv() {
        const token = localStorage.getItem('adminToken');
        if (!token || token === 'undefined') {
            this.setState({ redirect: this.loginUrl });
            return;
        }

        this.setState({ loading: true, errorMsg: '' });
        try {
            const requestPageSize = 200;
            const firstResponse = await this.fetchProductPage(token, 1, requestPageSize);
            if (!firstResponse || !firstResponse.data) {
                this.setState({ loading: false, errorMsg: 'CSV татах өгөгдөл олдсонгүй.' });
                return;
            }

            const allRows: any[] = [...firstResponse.data];
            const totalPages = Math.max(1, Number(firstResponse.page?.totalPages) || 1);

            for (let page = 2; page <= totalPages; page += 1) {
                const pageResponse = await this.fetchProductPage(token, page, requestPageSize);
                if (!pageResponse || !pageResponse.data) {
                    continue;
                }
                allRows.push(...pageResponse.data);
            }

            if (!allRows.length) {
                this.setState({ loading: false, errorMsg: 'CSV татах өгөгдөл олдсонгүй.' });
                return;
            }

            const selectedColumns = this.csvColumns().filter((column) => this.productColVisible(column.id));

            const lines = [
                selectedColumns.map((column) => this.toCsvCell(column.header)).join(','),
                ...allRows.map((row, index) =>
                    selectedColumns.map((column) => this.toCsvCell(column.value(row, index))).join(',')
                ),
            ];
            const csvContent = lines.join('\n');
            const now = new Date();
            const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            this.downloadBlob(csvContent, `products-${date}.csv`, 'text/csv;charset=utf-8;');
            this.setState({ loading: false });
        } catch (error) {
            console.error(error);
            this.setState({ loading: false, errorMsg: 'CSV файл татах үед алдаа гарлаа.' });
        }
    }

    syncQueryParams() {
        const { history, location } = this.props as RouteComponentProps;
        const params = new URLSearchParams();
        if (this.state.value1 != null) {
            params.set('providerId', String(this.state.value1));
        }
        if (this.state.value2) {
            params.set('name', String(this.state.value2));
        }
        if (this.state.value3) {
            params.set('barCode', String(this.state.value3));
        }
        syncFilterQueryParams(params, rowsToFilterFields(this.state.productFilterRows ?? []));
        if (this.state.page.page > 1) {
            params.set('page', String(this.state.page.page));
        }
        if (this.state.page.size !== 15) {
            params.set('size', String(this.state.page.size));
        }
        const search = params.toString();
        const newSearch = search ? `?${search}` : '';
        if (location.search !== newSearch) {
            history.replace({ pathname: location.pathname, search: newSearch });
        }
    }

    componentDidMount() {
        this.previousNameValue = this.state.value2 || '';
        this.refresh();
        this.getProviders();
    }

    getProviders() {
        var token = localStorage.getItem('adminToken')
        if (token && token !== 'undefined') {
            fetch(process.env.REACT_APP_API_URL + "list/provider", {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token,
                    'Content-Type': 'application/json'
                }
            })
            .then(res => {
                if (res.status === 401) {
                    this.setState({ redirect: this.loginUrl})
                } else {
                    res.json().then(res => {
                        if (!res || !res.data) {
                            return;
                        }
                        let data: any[] = [];
                        res.data.forEach((c: any) => {
                            if (c.active) {
                                data.push({
                                    key: c.id,
                                    value: c.id,
                                    text: c.name
                                });
                            }
                        });
                        this.setState({ list1: data });
                    })
                }
            })
            .catch(err => console.error(err));
        }
        else {
            this.setState({ redirect: this.loginUrl });
        }
    }

    refresh() {
        var token = localStorage.getItem('adminToken')
        if (token && token !== 'undefined') {
            
            if (this.previousNameValue !== this.state.value2) {
                this.setState({ loading: true, page: Object.assign(this.state.page, { page: 1 }) });
            }
            else {
                this.setState({ loading: true })
            }
            fetch(process.env.REACT_APP_API_URL + "mongol/products", {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(
                    productListRequestBody(this.state, {
                        page: this.previousNameValue === this.state.value2 ? this.state.page.page : 1,
                        size: this.state.page.size,
                    })
                )
            })
            .then(res => {
                this.previousNameValue = this.state.value2;
                this.setState({ loading: false })
                if (res.status === 401) {
                    this.setState({ redirect: this.loginUrl})
                } else {
                    res.json().then(res => {
                        if (!res || !res.data) {
                            return;
                        }
                        let data: any[] = res.data;
                        data.forEach(b => { b.confirmingDelete = false; });
                        setTimeout(() => {
                            this.setState({ data: data, page: res.page }, () => this.syncQueryParams());
                        });
                    })
                }
            })
            .catch(err => {
                this.setState({ loading: false })
                console.error(err)
            });
        }
        else {
            this.setState({ redirect: this.loginUrl });
        }
    }

    clearFilters() {
        this.setState(
            {
                value1: null,
                value2: '',
                value3: '',
                productFilterRows: [],
                productFilterDraftRows: [],
                productFilterModalOpen: false,
                page: { ...this.state.page, page: 1 },
            },
            () => {
                this.previousNameValue = '';
                this.refresh();
            }
        );
    }

    openFilterModal() {
        this.setState({
            productFilterModalOpen: true,
            productFilterDraftRows: cloneFilterRows(this.state.productFilterRows ?? []),
        });
    }

    closeFilterModal() {
        this.setState({ productFilterModalOpen: false });
    }

    applyFilterModal() {
        const sanitized = sanitizeFilterRows(this.state.productFilterDraftRows ?? []);
        this.setState(
            {
                productFilterRows: sanitized,
                productFilterModalOpen: false,
                page: { ...this.state.page, page: 1 },
            },
            () => this.refresh()
        );
    }

    onPageSizeChange(e: any, { value }: any) {
        this.setState({ 
            page: { ...this.state.page, size: value, page: 1 }
        }, () => {
            this.refresh();
        });
    }

    openProductColumnsModal() {
        const v = this.state.productTableColumnVisibility ?? loadProductListColumnVisibility();
        this.setState({
            productColumnsModalOpen: true,
            productColumnsModalDraft: { ...v },
        });
    }

    closeProductColumnsModal() {
        this.setState({ productColumnsModalOpen: false });
    }

    applyProductColumnsModal() {
        const draft = this.state.productColumnsModalDraft ?? loadProductListColumnVisibility();
        saveProductListColumnVisibility(draft);
        this.setState({
            productTableColumnVisibility: draft,
            productColumnsModalOpen: false,
        });
    }

    productColVisible(id: ProductListColumnId): boolean {
        return this.state.productTableColumnVisibility?.[id] !== false;
    }

    render() {
        if (this.state.redirect) {
            return <Redirect to={this.state.redirect}/>
        }
        const filterCount = activeFilterCount(this.state.productFilterRows);
        return (
            <section className="page-admin full-width">
                <Dimmer active={this.state.loading} inverted>
                    <Loader size='medium'></Loader>
                </Dimmer>
                <h2>Бүтээгдэхүүн</h2>
                <Link to="/mongol/products/new">
                    <Button basic color='orange'>Шинээр үүсгэх</Button>
                </Link>

                <div className="width-100 margin-top-20 admin-product-list-filters">
                    <Form>
                        <Form.Group className="admin-product-list-filters-row">
                            <Form.Dropdown width={5} required placeholder='Нийлүүлэгч' fluid labeled clearable selection options={this.state.list1}
                                value={this.state.value1} onChange={this.onValue1Change} />
                            <Form.Input width={4} placeholder="бүтээгдэхүүний нэр" type="text" value={this.state.value2 || ""} onChange={this.onValue2Change}></Form.Input>
                            <Form.Input width={4} placeholder="бар код" type="text" value={this.state.value3 || ""} onChange={this.onValue3Change}></Form.Input>
                            <Form.Field
                                className="admin-product-list-filter-open-field"
                                width={3}
                                style={{ alignSelf: 'flex-end' }}
                            >
                                <Button
                                    type="button"
                                    fluid
                                    icon
                                    labelPosition="left"
                                    className="admin-product-list-btn-secondary"
                                    style={{ whiteSpace: 'nowrap' }}
                                    onClick={this.openFilterModal}
                                    title="Нарийвчилсан филтер"
                                >
                                    <Icon name="filter" />
                                    Филтер{filterCount > 0 ? ` (${filterCount})` : ''}
                                </Button>
                            </Form.Field>
                        </Form.Group>
                        <Form.Group className="admin-product-list-filters-row">
                            <Form.Field className="admin-product-list-search-field">
                                <Button className="admin-product-list-btn-primary" width={3} onClick={() => this.refresh()}><Icon name="search"/>Хайх</Button>
                            </Form.Field>
                            <Form.Field className="admin-product-list-filter-secondary" style={{ marginLeft: 'auto' }}>
                                <Button className="admin-product-list-btn-secondary" width={3} onClick={() => this.clearFilters()}><Icon name="cancel"/>Филтер цэвэрлэх</Button>
                                <Button
                                    type="button"
                                    className="admin-product-list-btn-secondary"
                                    onClick={this.downloadCsv}
                                    disabled={this.state.loading}
                                >
                                    <Icon name="download" />
                                    CSV татах
                                </Button>
                                <Button
                                    type="button"
                                    icon
                                    className="admin-product-list-btn-secondary"
                                    title="Хүснэгтийн баганууд"
                                    aria-label="Хүснэгтийн баганууд"
                                    onClick={this.openProductColumnsModal}
                                >
                                    <span>Баганууд</span>
                                    <Icon name="filter" />
                                </Button>
                            </Form.Field>
                        </Form.Group>
                    </Form>
                </div>

                <ProductListFilterModal
                    open={!!this.state.productFilterModalOpen}
                    draftRows={this.state.productFilterDraftRows ?? []}
                    onDraftChange={(rows) => this.setState({ productFilterDraftRows: rows })}
                    onClose={this.closeFilterModal}
                    onApply={this.applyFilterModal}
                />

                <ProductListColumnsModal
                    open={!!this.state.productColumnsModalOpen}
                    draft={this.state.productColumnsModalDraft ?? loadProductListColumnVisibility()}
                    onDraftChange={(next) => this.setState({ productColumnsModalDraft: next })}
                    onClose={this.closeProductColumnsModal}
                    onApply={this.applyProductColumnsModal}
                />

                {this.state.errorMsg ? <div className="error-msg">
                    <Icon name="warning circle"/>&nbsp;
                    {this.state.errorMsg}
                </div> : null}

                <p>Нийт {this.state.page.totalRows} бүтээгдэхүүн олдлоо</p>

                <div className={ADMIN_LIST_PAGINATION_ROW_CLASS}>
                    <Pagination activePage={this.state.page.page} totalPages={this.state.page.totalPages} onPageChange={this.onPageChange}></Pagination>
                    <div className="page-size-selector flex-div align-items-center">
                        <div className="flex-div align-items-center" style={{ marginRight: '10px' }}>ХУУДАСНЫ ХЭМЖЭЭ:</div>
                        <Dropdown
                            selection
                            upward
                            options={this.pageSizeOptions}
                            value={this.state.page.size}
                            onChange={this.onPageSizeChange}
                            placeholder="Хуудасны хэмжээ"
                            compact
                        />
                    </div>
                </div>

                <Table striped className="admin-list-table" width={16}>
                    <Table.Header>
                        <Table.Row>
                            {this.productColVisible('index') ? <Table.HeaderCell>№</Table.HeaderCell> : null}
                            {this.productColVisible('name') ? <Table.HeaderCell>Нэр</Table.HeaderCell> : null}
                            {this.productColVisible('provider') ? <Table.HeaderCell>Нийлүүлэгч</Table.HeaderCell> : null}
                            {this.productColVisible('price') ? <Table.HeaderCell>Үнэ</Table.HeaderCell> : null}
                            {this.productColVisible('clubPrice') ? <Table.HeaderCell>Клубын үнэ</Table.HeaderCell> : null}
                            {this.productColVisible('salePrice') ? <Table.HeaderCell>Хямдралтай үнэ</Table.HeaderCell> : null}
                            {this.productColVisible('saleUntil') ? <Table.HeaderCell>Хямдралын сүүлийн өдөр</Table.HeaderCell> : null}
                            {this.productColVisible('deliveryCost') ? <Table.HeaderCell>Хүргэлтийн зардал</Table.HeaderCell> : null}
                            {this.productColVisible('size') ? <Table.HeaderCell>Хэмжээ</Table.HeaderCell> : null}
                            {this.productColVisible('count') ? <Table.HeaderCell>Үлдэгдэл</Table.HeaderCell> : null}
                            {this.productColVisible('stockLink') ? <Table.HeaderCell></Table.HeaderCell> : null}
                            {this.productColVisible('importCost') ? <Table.HeaderCell>Өртөг тооцдог</Table.HeaderCell> : null}
                            {this.productColVisible('deliveryDayMargin') ? <Table.HeaderCell>Хүргэх өдөр</Table.HeaderCell> : null}
                            {this.productColVisible('barCode') ? <Table.HeaderCell>Бар код</Table.HeaderCell> : null}
                            {this.productColVisible('slugLink') ? <Table.HeaderCell>Линк</Table.HeaderCell> : null}
                            {this.productColVisible('active') ? <Table.HeaderCell>Идэвхитэй</Table.HeaderCell> : null}
                            {this.productColVisible('actions') ? <Table.HeaderCell></Table.HeaderCell> : null}
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        { this.state?.data?.map((row: any, index: number) => {
                                return <Table.Row key={row.id}>
                                    {this.productColVisible('index') ? <Table.Cell data-label="№">{ String(index+1) }</Table.Cell> : null}
                                    {this.productColVisible('name') ? (
                                    <Table.Cell data-label="Нэр">
                                        <strong>
                                            <Link className="underline hover-blue" to={"/mongol/product/" + row.id}>{ row.name }</Link>
                                        </strong>
                                    </Table.Cell>
                                    ) : null}
                                    {this.productColVisible('provider') ? (
                                    <Table.Cell data-label="Нийлүүлэгч">
                                        <Link className="underline" to={"/mongol/provider/" + row.providerId}>{row.providerName}</Link>
                                    </Table.Cell>
                                    ) : null}
                                    {this.productColVisible('price') ? <Table.Cell data-label="Үнэ">{row.saleActive ? <span className="striked">{row.price} ₮</span>: null} {row.saleActive ? row.salePrice : row.price} ₮</Table.Cell> : null}
                                    {this.productColVisible('clubPrice') ? <Table.Cell data-label="Клубын үнэ">{row.clubPrice} ₮</Table.Cell> : null}
                                    {this.productColVisible('salePrice') ? <Table.Cell data-label="Хямдралтай үнэ">{row.salePrice} ₮</Table.Cell> : null}
                                    {this.productColVisible('saleUntil') ? (
                                        <Table.Cell data-label="Хямдралын сүүлийн өдөр">{formatProductSaleUntil(row.saleUntil)}</Table.Cell>
                                    ) : null}
                                    {this.productColVisible('deliveryCost') ? (
                                    <Table.Cell
                                        data-label="Хүргэлтийн зардал"
                                        style={{
                                            color:
                                                row.deliveryCost > 0 ? 'inherit' : 'var(--admin-text-muted)',
                                        }}
                                    >
                                        {row.deliveryCost > 0 ? `${row.deliveryCost} ₮` : '-'}
                                    </Table.Cell>
                                    ) : null}
                                    {this.productColVisible('size') ? <Table.Cell data-label="Хэмжээ"> {row.size} {row.unit} </Table.Cell> : null}
                                    {this.productColVisible('count') ? <Table.Cell data-label="Үлдэгдэл"> {row.count} </Table.Cell> : null}
                                    {this.productColVisible('stockLink') ? (
                                    <Table.Cell data-label="Үлдэгдэл">
                                        <Link
                                            className="underline"
                                            to={row.import
                                                ? '/mongol/warehouse/' + row.warehouseId + '?from=products'
                                                : '/mongol/warehouse/' + row.warehouseId + '/count?from=products'
                                            }
                                        >
                                            Үлдэгдэл&nbsp;<Icon name="pencil"/>
                                        </Link>
                                    </Table.Cell>
                                    ) : null}
                                    {this.productColVisible('importCost') ? <Table.Cell data-label="Өртөг тооцдог">{ row.import ? <Icon name="checkmark"/> : null }</Table.Cell> : null}
                                    {this.productColVisible('deliveryDayMargin') ? <Table.Cell data-label="Хүргэх өдөр"> {row.deliveryDayMargin}</Table.Cell> : null}
                                    {this.productColVisible('barCode') ? <Table.Cell data-label="Бар код"> {row.barCode} </Table.Cell> : null}
                                    {this.productColVisible('slugLink') ? (
                                    <Table.Cell data-label="Линк">
                                        {row.slug ? (
                                            <a 
                                                href={`/product/${row.slug}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="underline"
                                            >
                                                <Icon name="external" />
                                            </a>
                                        ) : null}
                                    </Table.Cell>
                                    ) : null}
                                    {this.productColVisible('active') ? <Table.Cell data-label="Идэвхитэй"> { row.active ? <Icon name="checkmark"/> : null } </Table.Cell> : null}
                                    {this.productColVisible('actions') ? (
                                    <Table.Cell data-label="Үйлдэл">
                                        { row.confirmingDelete ? 
                                            <React.Fragment>
                                                <Button primary size='mini' onClick={() => this.delete(index)}>Устга</Button>
                                                <Button size='mini' onClick={() => this.toggleDelete(index)}>Болих</Button>
                                            </React.Fragment> :
                                            <Icon name="trash" className="cursor chef-icon-hover padding-5" onClick={() => this.toggleDelete(index)}/>
                                        }
                                    </Table.Cell>
                                    ) : null}
                                </Table.Row>
                            })
                        }
                    </Table.Body>
                </Table>

                <div className={ADMIN_LIST_PAGINATION_ROW_CLASS}>
                    <Pagination activePage={this.state.page.page} totalPages={this.state.page.totalPages} onPageChange={this.onPageChange}></Pagination>
                    <div className="page-size-selector flex-div align-items-center">
                        <div className="flex-div align-items-center" style={{ marginRight: '10px' }}>ХУУДАСНЫ ХЭМЖЭЭ:</div>
                        <Dropdown
                            selection
                            upward
                            options={this.pageSizeOptions}
                            value={this.state.page.size}
                            onChange={this.onPageSizeChange}
                            placeholder="Хуудасны хэмжээ"
                            compact
                        />
                    </div>
                </div>
            </section>
        );
    }
}
            
export default withRouter(AdminProductList as unknown as React.ComponentType<RouteComponentProps>);
